import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../../components/ui/Toast';
import { usePreventBack } from '../../hooks/usePreventBack';
import { useScreeningLeaveWarning } from '../../hooks/useScreeningLeaveWarning';
import logger from '../../utils/logger';
import { saveAiInterviewTakeMinimalBackup } from '../../lib/publicAssessmentFlow';
import { useScreeningExpiryRedirect } from '../../hooks/useScreeningExpiryRedirect';
import {
  UserIcon,
  EnvelopeIcon,
  MicrophoneIcon,
  ClockIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  SignalIcon,
  CheckCircleIcon,
  CheckIcon,
  SparklesIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  QuestionMarkCircleIcon,
  ExclamationCircleIcon,
  SpeakerWaveIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'src/components/ui/ui/tooltip';
import LeaveScreeningModal from '../../components/ui/LeaveScreeningModal';
import DeviceCheckModal from '../../components/ui/DeviceCheckModal';
import InterviewReadinessModal from '../../components/interview/InterviewReadinessModal';
import { isDeviceRecentlyChecked } from '../../utils/deviceCheckMemory';
import { ADVANCED_INTERVIEW_TYPE_LABELS } from 'src/constants/advancedInterviewTypes';
import { useCandidateLanguage } from '../../i18n/candidate';

const STEP_SECONDARY = {
  'Basic details': 'Share your contact information',
  'Resume upload': 'Upload your latest resume',
  Assessment: 'Complete skills assessment',
  'AI interview': 'Answer structured questions',
  'Document upload': 'Upload supporting documents (optional)',
};

const ADVANCED_STEP_SECONDARY = {
  'reading-assessment': 'Read passages aloud and answer questions',
  'code-interview': 'Write code and discuss your approach',
  'case-study': 'Analyze a business scenario',
  'resume-deep-dive': 'Discuss your resume and experience',
  'multi-language': 'Demonstrate language proficiency',
  'avatar': 'Interview with an AI avatar',
};

function getInterviewStepSecondary(advancedType) {
  if (advancedType && ADVANCED_STEP_SECONDARY[advancedType]) {
    return ADVANCED_STEP_SECONDARY[advancedType];
  }
  return STEP_SECONDARY['AI interview'];
}

const getStepMeta = (interviewType) => ({
  'Basic details': {
    icon: UserIcon,
    desc: '',
    gradient: 'from-blue-500 to-indigo-600',
  },
  'Resume upload': {
    icon: DocumentTextIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
  Assessment: {
    icon: AcademicCapIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
  'AI interview': {
    icon: interviewType === 'video' ? VideoCameraIcon : MicrophoneIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
  'Document upload': {
    icon: DocumentTextIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
  'Application form': {
    icon: ClipboardDocumentListIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
});

function getInterviewStepLabel(interviewType) {
  if (interviewType === 'video') return 'AI video interview';
  if (interviewType === 'voice' || interviewType === 'audio') return 'AI voice interview';
  return 'AI interview';
}

const AIInterviewJoin = () => {
  const { sharingLink } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { error: showError } = useToast();
  const prefilledEmail = searchParams.get('email') || '';
  const prefilledName = searchParams.get('name') || '';
  const hasAssessment = searchParams.get('hasAssessment') === 'true' || location.state?.hasAssessment === true;
  const screeningIdFromQuery = searchParams.get('screeningId') || location.state?.screeningId || null;
  const [hasResumeScoring, setHasResumeScoring] = useState(screeningIdFromQuery ? null : false);
  const [resumeStepResolved, setResumeStepResolved] = useState(!screeningIdFromQuery);
  const [includeDocumentCollection, setIncludeDocumentCollection] = useState(false);
  const [includeApplicationForm, setIncludeApplicationForm] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  usePreventBack(!!screeningIdFromQuery, { onBackAttempt: useCallback(() => setShowLeaveModal(true), []) });
  useScreeningLeaveWarning(!!screeningIdFromQuery);
  const isEmailLocked = Boolean(prefilledEmail);
  const isNameLocked = Boolean(prefilledName);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const t = useCandidateLanguage({ screeningId: screeningIdFromQuery, interviewId: interview?._id });

  // Fetch screening config (resume scoring flag) for accurate timeline display
  useEffect(() => {
    if (!screeningIdFromQuery) {
      setHasResumeScoring(false);
      setResumeStepResolved(true);
      setIncludeDocumentCollection(false);
      return;
    }
    let cancelled = false;
    setResumeStepResolved(false);
    const emailFromUrl =
      searchParams.get('email')?.trim().toLowerCase() || prefilledEmail.trim().toLowerCase();
    const params = new URLSearchParams();
    if (emailFromUrl) params.set('email', emailFromUrl);
    axios
      .get(`/api/screenings/${screeningIdFromQuery}/apply-info${params.toString() ? `?${params.toString()}` : ''}`)
      .then((res) => {
        if (cancelled) return;
        const payload = res.data || {};
        setHasResumeScoring(payload.hasResumeScoring !== false);
        setIncludeDocumentCollection(!!payload.includeDocumentCollection);
        setIncludeApplicationForm(!!payload.includeApplicationForm);
        setResumeStepResolved(true);
      })
      .catch(() => {
        if (!cancelled) {
          setHasResumeScoring(false);
          setIncludeDocumentCollection(false);
          setResumeStepResolved(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [screeningIdFromQuery, searchParams, prefilledEmail, location.state]);

  const [isTestingAudio, setIsTestingAudio] = useState(false);
  const [audioTestState, setAudioTestState] = useState(null); // null, 'testing', 'recording', 'playing', 'success', 'error'
  const [audioLevel, setAudioLevel] = useState(0);
  const [recordedAudioBlob, setRecordedAudioBlob] = useState(null);
  const [checkedItems, setCheckedItems] = useState({
    browser: false,
    recording: false,
    privacy: false,
  });
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [showDeviceCheck, setShowDeviceCheck] = useState(false);
  const [showReadinessCheck, setShowReadinessCheck] = useState(false);
  const [timeLeftSeconds, setTimeLeftSeconds] = useState(10 * 60);
  const hasAutoJoinAttempted = useRef(false);
  useScreeningExpiryRedirect({
    screeningId: screeningIdFromQuery,
    email: searchParams.get('email') || location.state?.candidateInfo?.email || email || '',
    enabled: !!screeningIdFromQuery,
  });
  // Pre-start timer (10 minutes). When it expires we disable actions on this page.
  const timerExpired = timeLeftSeconds <= 0;

  // No session-end beacon on interview join: same issue as assessment join (force-complete on refresh).

  // Countdown effect – only once interview metadata is available
  useEffect(() => {
    if (!interview || timerExpired) return;
    const interval = setInterval(() => {
      setTimeLeftSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [interview, timerExpired]);

  useEffect(() => {
    if (timerExpired && screeningIdFromQuery) {
      showError(t('Session Expired'), t('Your session expired. Please confirm your details to restart.'));
      const urlParams = new URLSearchParams();
      const emailParam = searchParams.get('email') || email || location.state?.candidateInfo?.email;
      const nameParam = searchParams.get('name') || name || location.state?.candidateInfo?.name;
      
      if (emailParam && screeningIdFromQuery) {
        axios.post(`/api/screenings/${screeningIdFromQuery}/local-timeout`, { email: emailParam.trim() }).catch(err => console.error('Failed to report local timeout:', err));
      }

      if (emailParam) urlParams.set('email', emailParam.trim());
      if (nameParam) urlParams.set('name', nameParam.trim());
      navigate(`/apply/${screeningIdFromQuery}?${urlParams.toString()}`, { replace: true });
    }
  }, [timerExpired, screeningIdFromQuery, navigate, email, name, searchParams, location.state, showError, t]);

  // Helper function to summarize job description (1-2 lines)
  const summarizeJobDescription = (description) => {
    if (!description || typeof description !== 'string') return null;
    // Remove extra whitespace and newlines
    const cleaned = description.trim().replace(/\s+/g, ' ');
    if (!cleaned || cleaned.length === 0) return null;
    // If already short (under 200 chars), return as is
    if (cleaned.length <= 200) return cleaned;
    // Otherwise, take first 200 chars and add ellipsis
    return cleaned.substring(0, 200).trim() + '...';
  };

  // Get role overview text (job description summary or fallback) - using useMemo to ensure latest data
  const getRoleOverview = useMemo(() => {
    if (!interview) return t('Loading...');

    // Log for debugging - comprehensive check
    logger.log('🔍 Getting Role Overview - Full Interview Object:', interview);
    logger.log('🔍 Job Posting Object:', interview.jobPosting);
    logger.log('🔍 Job Posting Type:', typeof interview.jobPosting);
    logger.log('🔍 Job Posting Description:', interview.jobPosting?.description);
    logger.log('🔍 Description Type:', typeof interview.jobPosting?.description);
    logger.log('🔍 Description Length:', interview.jobPosting?.description?.length);

    const debugInfo = {
      hasJobPosting: !!interview.jobPosting,
      jobPostingIsNull: interview.jobPosting === null,
      jobPostingIsUndefined: interview.jobPosting === undefined,
      hasDescription: !!interview.jobPosting?.description,
      descriptionLength: interview.jobPosting?.description?.length || 0,
      descriptionPreview: interview.jobPosting?.description?.substring(0, 100) || 'N/A',
      hasContext: !!interview.context,
      contextPreview: interview.context?.substring(0, 100) || 'N/A',
    };
    logger.log('🔍 Debug Info:', debugInfo);

    // Priority 1: Job posting description (AI summarized)
    if (interview.jobPosting && interview.jobPosting.description) {
      const description = interview.jobPosting.description;
      logger.log('✅ Found Job Description, length:', description.length);
      const summary = summarizeJobDescription(description);
      if (summary) {
        logger.log('✅ Using Job Description Summary:', summary.substring(0, 100));
        return summary;
      } else {
        logger.log('⚠️ summarizeJobDescription returned null/empty');
      }
    } else {
      logger.log('⚠️ No job posting or description found');
      if (!interview.jobPosting) {
        logger.log('⚠️ interview.jobPosting is:', interview.jobPosting);
      } else if (!interview.jobPosting.description) {
        logger.log('⚠️ interview.jobPosting.description is:', interview.jobPosting.description);
      }
    }

    // Priority 2: Interview context
    if (interview.context) {
      logger.log('⚠️ Using Interview Context as fallback');
      return interview.context;
    }

    // Priority 3: Last fallback
    logger.log('⚠️ Using default fallback message');
    return t('Role description will be provided during the interview.');
  }, [interview, t]);

  const fetchInterview = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      const response = await axios.get(`/api/ai-interviews/share/${sharingLink}`);
      logger.log('AI Interview data received:', response.data.interview);
      logger.log('Job Posting data:', response.data.interview.jobPosting);
      logger.log('Job Description:', response.data.interview.jobPosting?.description);
      logger.log('Invited candidates:', response.data.interview.invitedCandidates);
      setInterview(response.data.interview);
    } catch (error) {
      console.error('Error fetching AI interview:', error);
      if (error.response?.status === 404) {
        setErrorMessage('AI Interview not found or no longer available');
      } else {
        setErrorMessage('Failed to load AI interview. Please check the link and try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [sharingLink]);

  useEffect(() => {
    if (sharingLink) {
      fetchInterview();
    }
  }, [sharingLink, fetchInterview]);

  // Prefill name/email from URL (hub link) or from navigation state (screening apply)
  useEffect(() => {
    const emailFromUrl = searchParams.get('email') || '';
    const nameFromUrl = searchParams.get('name') || '';
    const fromUrl = {
      ...(emailFromUrl && { email: emailFromUrl }),
      ...(nameFromUrl && { name: nameFromUrl }),
    };
    const fromState = location.state?.candidateInfo || {};
    const merged = { ...fromUrl, ...fromState };
    if (merged.name) setName(merged.name);
    if (merged.email) setEmail(merged.email);
  }, [searchParams, location.state]);

  // When user arrives with prefilled name/email (e.g. from "Take me to AI Interview"), auto-join and go straight to "Start your interview"
  const hasPrefilledCredentials =
    (prefilledName && prefilledEmail) ||
    (location.state?.candidateInfo?.name && location.state?.candidateInfo?.email);

  const doJoin = useCallback(async () => {
    if (!name.trim() || !email.trim()) {
      setErrorMessage('Please provide both name and email');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    if (!interview) return;
    if (screeningIdFromQuery) {
      try {
        const emailFromUrl =
          searchParams.get('email') || email.trim() || location.state?.candidateInfo?.email || '';
        const params = new URLSearchParams();
        if (emailFromUrl) params.set('email', emailFromUrl.trim().toLowerCase());
        const res = await axios.get(
          `/api/screenings/${screeningIdFromQuery}/apply-info${params.toString() ? `?${params.toString()}` : ''}`
        );
        const payload = res.data || {};
        const deadline = payload.screeningDeadline || null;
        const timeUp = !!payload.timeUp;
        const parsed = deadline ? Date.parse(deadline) : NaN;
        const expiredFromClock = deadline ? !Number.isNaN(parsed) && Date.now() >= parsed : false;
        if (timeUp || expiredFromClock) {
          setErrorMessage('Your screening window has ended. You can no longer start this AI interview.');
          return;
        }
      } catch (_) {
        // Non-blocking: allow attempt; server will enforce if time is actually up.
      }
    }

    try {
      setAuthenticating(true);
      setErrorMessage(null);
      // Start the interview session
      const res = await axios.post('/api/ai-interviews/session/start', {
        sharingLink,
        candidateName: name,
        candidateEmail: email,
      });

      const sessionId = res.data?.sessionId;
      const sessionToken = res.data?.sessionToken;
      const opening = res.data?.opening;
      if (!sessionId) throw new Error('No session');

      setSessionData({
        sessionId,
        sessionToken,
        opening,
        maxQuestions: res.data?.maxQuestions,
        voiceModel: res.data?.voiceModel,
        useEnhancedAI: res.data?.useEnhancedAI,
        deliveryMode: 'livekit', // Always LiveKit - batch mode is deprecated
        readinessCheck: res.data?.readinessCheck === true,
      });
      setIsAuthenticated(true);
    } catch (e) {
      console.error('Error starting AI interview:', e);
      if (e.response?.data?.code === 'NOT_INVITED') {
        setErrorMessage(
          'You are not invited to take this AI interview. Please contact the interview creator.'
        );
      } else if (e.response?.data?.message) {
        setErrorMessage(e.response.data.message);
      } else {
        setErrorMessage('Failed to start AI interview. Please try again.');
      }
    } finally {
      setAuthenticating(false);
    }
  }, [sharingLink, name, email, interview]);

  useEffect(() => {
    if (
      interview &&
      hasPrefilledCredentials &&
      name &&
      email &&
      !isAuthenticated &&
      !authenticating &&
      !hasAutoJoinAttempted.current
    ) {
      hasAutoJoinAttempted.current = true;
      doJoin();
    }
  }, [interview, hasPrefilledCredentials, name, email, isAuthenticated, authenticating, doJoin]);

  const handleJoin = (e) => {
    e?.preventDefault?.();
    doJoin();
  };

  // Check if permissions are already granted; if yes skip modal, otherwise show it
  const startInterview = async () => {
    // Org feature ai_interview_readiness_check: the voice + camera check always runs (no
    // recently-checked skip); it also covers mic/camera permission.
    if (sessionData?.readinessCheck && sessionData?.sessionId && sessionData?.sessionToken) {
      setShowReadinessCheck(true);
      return;
    }
    const isVideo = interview?.type === 'video';
    try {
      // SMART SKIP: Check if device was checked recently (within 2 hours) on the Apply page
      const recentlyChecked = isDeviceRecentlyChecked();

      let micGranted = false;
      let camGranted = !isVideo; // camera not needed for voice-only

      if (recentlyChecked) {
        // Assume granted if recently checked to bypass the Permissions API check which can be flaky
        micGranted = true;
        camGranted = true;
      } else {
        // 1. Try the Permissions API (supported in Chrome, Edge, Opera)
        if (navigator.permissions && navigator.permissions.query) {
          try {
            const micPerm = await navigator.permissions.query({ name: 'microphone' });
            micGranted = micPerm.state === 'granted';
            if (isVideo) {
              const camPerm = await navigator.permissions.query({ name: 'camera' });
              camGranted = camPerm.state === 'granted';
            }
          } catch (_) {
            // Firefox/Safari don't support querying mic/camera — fall through
          }
        }
      }

      // 2. If Permissions API confirmed granted OR we recently checked it manually, do a quick probe to be 100% sure
      if (micGranted && camGranted) {
        const constraints = isVideo ? { audio: true, video: true } : { audio: true };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        stream.getTracks().forEach((t) => t.stop());
        // All good — skip modal, go straight to interview
        proceedToInterview();
        return;
      }

      // 3. Permissions not confirmed — show the device check modal
      setShowDeviceCheck(true);
    } catch (_) {
      // getUserMedia failed even though Permissions API said granted — show modal
      setShowDeviceCheck(true);
    }
  };

  // Called after device check passes — proceed to the actual interview
  const proceedToInterview = () => {
    setShowDeviceCheck(false);
    setShowReadinessCheck(false);
    saveAiInterviewTakeMinimalBackup(sharingLink, {
      email,
      name,
      screeningId: screeningIdFromQuery,
    });
    const takeQs = new URLSearchParams();
    takeQs.set('email', email.trim());
    takeQs.set('name', name.trim());
    if (screeningIdFromQuery) takeQs.set('screeningId', screeningIdFromQuery);
    const takeSuffix = takeQs.toString() ? `?${takeQs.toString()}` : '';
    navigate(`/ai-interview/${sharingLink}/take${takeSuffix}`, {
      state: {
        sharingLink,
        name,
        email,
        interview,
        sessionId: sessionData?.sessionId,
        sessionToken: sessionData?.sessionToken,
        opening: sessionData?.opening,
        maxQuestions: sessionData?.maxQuestions,
        voiceModel: sessionData?.voiceModel,
        useEnhancedAI: sessionData?.useEnhancedAI,
        deliveryMode: 'livekit',
        hasAssessment,
        screeningId: screeningIdFromQuery,
      },
    });
  };

  const handleGoBack = () => {
    // Reset authentication state to go back to Page 1
    // Keep name and email so user can edit them
    setIsAuthenticated(false);
    setSessionData(null);
    // Reset checkboxes
    setCheckedItems({
      browser: false,
      recording: false,
      privacy: false,
    });
  };

  const testAudio = async () => {
    setIsTestingAudio(true);
    setAudioTestState('testing');
    setAudioLevel(0);

    let stream = null;
    let mediaRecorder = null;
    let audioContext = null;
    let analyser = null;
    let microphone = null;
    let animationFrameId = null;
    const chunks = [];

    try {
      // Request microphone access
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioTestState('recording');

      // Set up audio analysis for visual feedback
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      analyser = audioContext.createAnalyser();
      microphone = audioContext.createMediaStreamSource(stream);
      analyser.fftSize = 256;
      microphone.connect(analyser);

      // Start visual feedback loop
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateAudioLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(average / 255); // Normalize to 0-1
        if (audioTestState === 'recording' || audioTestState === 'testing') {
          animationFrameId = requestAnimationFrame(updateAudioLevel);
        }
      };
      updateAudioLevel();

      // Set up MediaRecorder for playback
      mediaRecorder = new MediaRecorder(stream);
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setRecordedAudioBlob(blob);
        setAudioTestState('playing');

        // Play back the recorded audio
        const audioUrl = URL.createObjectURL(blob);
        const audio = new Audio(audioUrl);
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          setAudioTestState('success');
          setTimeout(() => {
            setAudioTestState(null);
            setIsTestingAudio(false);
          }, 2000);
        };
        audio.onerror = () => {
          setAudioTestState('error');
          setTimeout(() => {
            setAudioTestState(null);
            setIsTestingAudio(false);
          }, 2000);
        };
        await audio.play();
      };

      // Record for 3 seconds
      mediaRecorder.start();
      await new Promise((resolve) => setTimeout(resolve, 3000));
      mediaRecorder.stop();

      // Clean up stream after recording
      stream.getTracks().forEach((track) => track.stop());
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (audioContext) audioContext.close();
    } catch (error) {
      setAudioTestState('error');
      console.error('Audio test error:', error);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (stream) stream.getTracks().forEach((track) => track.stop());
      if (audioContext) audioContext.close();
      setTimeout(() => {
        setAudioTestState(null);
        setIsTestingAudio(false);
      }, 3000);
    }
  };

  // Footer Component (reusable)
  const Footer = () => (
    <div className="text-center mt-3 sm:mt-4 md:mt-6 text-gray-600 text-xs sm:text-sm space-y-1">
      <p>{t('Your privacy matters. This interview is secure and confidential.')}</p>
      <p>
        {t('Need Help?')}{' '}
        <a
          href="mailto:admin@indaratech.net"
          className="text-purple-600 hover:text-purple-700 underline"
        >
          {t('Contact Support')}
        </a>
      </p>
    </div>
  );

  // Calculate duration estimate - use 70% for more realistic estimate (closer to actual time)
  const duration = interview?.config?.duration || 30;
  const estimatedDuration = Math.max(1, Math.floor(duration * 0.7));
  const durationRange = `${estimatedDuration}`;
  const fullDuration = duration;

  const compactHeader = (
    <header className="flex items-center justify-between gap-3">
      <div className="flex flex-col min-w-0">
        <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('Powered by')}</p>
        <div className="mt-0.5 flex items-center gap-1.5">
          <img src="/logo.svg" alt="TalentCrux" className="h-9 w-9 sm:h-12 sm:w-12 md:h-14 md:w-14 object-contain flex-shrink-0" />
          <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate">TalentCrux</span>
        </div>
      </div>
      <div className="hidden sm:flex items-center justify-end flex-shrink-0 min-w-0" aria-hidden />
    </header>
  );

  // Early returns – same layout as screening steps (header + bg); compact on mobile
  if (loading) {
    return (
      <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
        <div className="mx-auto max-w-6xl">
          {compactHeader}
        </div>
        <div className="mx-auto max-w-3xl flex items-center justify-center mt-8 sm:mt-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-electric-blue mx-auto mb-3 sm:mb-4" />
            <p className="text-xs text-gray-600 sm:text-sm">{t('Loading AI interview…')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
        <div className="mx-auto max-w-6xl">
          {compactHeader}
          <div className="mt-4 sm:mt-8 max-w-md mx-auto">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 lg:p-8 text-center">
              <div className="bg-red-100 rounded-full p-2.5 sm:p-3 mx-auto mb-3 sm:mb-4 w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center">
                <MicrophoneIcon className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-1.5 sm:mb-2 sm:text-xl">{t('AI Interview Not Available')}</h2>
              <p className="text-xs text-gray-600 mb-4 sm:mb-6 sm:text-sm">{t(errorMessage)}</p>
              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center justify-center rounded-lg bg-gray-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors sm:px-6 sm:py-2.5"
              >
                {t('Go Back')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!interview) {
    return (
      <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
        <div className="mx-auto max-w-6xl">
          {compactHeader}
          <div className="mt-4 sm:mt-8 max-w-md mx-auto">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 lg:p-8 text-center">
              <h2 className="text-lg font-bold text-gray-900 mb-1.5 sm:mb-2 sm:text-xl">{t('AI Interview Not Found')}</h2>
              <p className="text-xs text-gray-600 sm:text-sm">{t("The AI interview you're looking for doesn't exist or is no longer available.")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const jobTitle = interview.jobPosting?.title || interview.title;
    const companyName =
      interview.jobPosting?.company || interview.createdBy?.company || t('Your recruiter');
    const hasDetails =
      interview.context ||
      (interview.jobPosting && interview.jobPosting.description) ||
      getRoleOverview;

    const showPreparingInsteadOfForm = hasPrefilledCredentials && !errorMessage;

    if (showPreparingInsteadOfForm) {
      return (
        <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
          <div className="mx-auto max-w-6xl">
            {compactHeader}
          </div>
          <div className="mx-auto max-w-3xl flex items-center justify-center mt-8 sm:mt-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-electric-blue mx-auto mb-3 sm:mb-4" />
              <p className="text-base font-semibold text-gray-900 sm:text-lg">{t('Preparing your interview…')}</p>
              <p className="mt-1.5 sm:mt-2 text-xs text-gray-600 sm:text-sm">{t('Taking you to the start screen.')}</p>
            </div>
          </div>
        </div>
      );
    }

    const includeResumeStep = hasResumeScoring && resumeStepResolved;
    const steps = [
      'Basic details',
      ...(includeResumeStep ? ['Resume upload'] : []),
      ...(includeDocumentCollection ? ['Document upload'] : []),
      ...(includeApplicationForm ? ['Application form'] : []),
      ...(hasAssessment ? ['Assessment'] : []),
      'AI interview',
    ];
    const currentStepIndex = steps.findIndex((s) => s === 'AI interview');
    const effectiveInterviewType = interview?.type === 'video' ? 'video' : 'voice';
    const stepMetaMap = getStepMeta(effectiveInterviewType);

    return (
      <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
        <LeaveScreeningModal
          isOpen={showLeaveModal}
          onClose={() => setShowLeaveModal(false)}
          onStay={() => setShowLeaveModal(false)}
          onLeave={() => {
            setShowLeaveModal(false);
            navigate(-1);
          }}
          title={t('Leave this page?')}
          message={t('Refreshing or closing this page may auto-submit your assessment and affect your progress. Please stay on this page until you complete this step or your time runs out.')}
        />
        <div className="mx-auto max-w-6xl">
          {compactHeader}

          {steps.length > 0 && (
            <section className="mt-6 sm:mt-8">
              <p className="text-center text-[11px] sm:text-sm font-semibold uppercase tracking-[0.22em] text-gray-500 mb-5 sm:mb-7">
                {t('Your screening journey')}
              </p>
              <div className="flex items-start justify-center gap-0">
                {steps.map((label, index) => {
                  const meta =
                    stepMetaMap[label] || {
                      icon: CheckIcon,
                      desc: label === 'AI interview' ? getInterviewStepSecondary(interview?.advancedType) : (STEP_SECONDARY[label] || ''),
                      gradient: 'from-gray-400 to-gray-500',
                    };
                  const StepIcon = meta.icon;
                  const isActive = index === currentStepIndex;
                  const isCompleted = index < currentStepIndex;
                  return (
                    <React.Fragment key={label}>
                      <div className="flex flex-col items-center text-center min-w-0 flex-1 sm:flex-none sm:w-32">
                        <span
                          className={`text-[9px] sm:text-xs font-semibold uppercase tracking-[0.06em] sm:tracking-[0.16em] whitespace-nowrap mb-1.5 ${
                            isActive ? 'text-electric-blue' : isCompleted ? 'text-emerald-400' : 'text-gray-300'
                          }`}
                        >
                          {t('Step {number}', { number: index + 1 })}
                        </span>
                        <div
                          className={`relative flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center rounded-2xl transition-all duration-500 ${
                            isActive
                              ? `bg-gradient-to-br ${meta.gradient} text-white shadow-lg`
                              : isCompleted
                              ? 'bg-emerald-50 text-emerald-500 border-2 border-emerald-200'
                              : 'bg-white text-gray-300 border-2 border-gray-200 border-dashed'
                          }`}
                          style={
                            isActive
                              ? { boxShadow: '0 8px 25px rgba(0, 123, 255, 0.2)' }
                              : isCompleted
                              ? { boxShadow: '0 6px 18px rgba(16, 185, 129, 0.18)' }
                              : {}
                          }
                        >
                          {isCompleted ? (
                            <CheckIcon className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.5} />
                          ) : (
                            <StepIcon className="h-5 w-5 sm:h-7 sm:w-7" strokeWidth={isActive ? 2 : 1.5} />
                          )}
                          {isActive && (
                            <span
                              className="absolute inset-0 rounded-2xl border-2 border-electric-blue/30 animate-ping"
                              style={{ animationDuration: '2s' }}
                            />
                          )}
                        </div>
                        <p
                          className={`mt-2 text-[10px] sm:text-sm font-semibold leading-tight hyphens-auto [overflow-wrap:anywhere] ${
                            isActive ? 'text-gray-900' : isCompleted ? 'text-emerald-600' : 'text-gray-400'
                          }`}
                        >
                          {t(label === 'AI interview' ? getInterviewStepLabel(effectiveInterviewType) : label)}
                        </p>
                      </div>
                      {index < steps.length - 1 && (
                        <div className="flex-1 flex items-center pt-[52px] sm:pt-[52px] min-w-[6px] max-w-[12px] sm:max-w-[72px]">
                          <div className="w-full flex items-center gap-0.5">
                            {[...Array(4)].map((_, i) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <div
                                key={i}
                                className={`flex-1 h-0.5 rounded-full ${
                                  index < currentStepIndex ? 'bg-emerald-400/60' : 'bg-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </section>
          )}

          {/* Main interview intro card – aligned with assessment theme */}
          <div className="mt-4 sm:mt-6 max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
              {/* Header strip */}
              <div className="bg-electric-blue px-5 py-4 sm:px-6 sm:py-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                      {t('Start your interview')}
                    </h1>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 sm:px-3.5 sm:py-1.5 text-[10px] sm:text-xs font-semibold text-electric-blue-700 shadow-sm cursor-default whitespace-nowrap">
                            <ClockIcon className="h-4 w-4 text-electric-blue-500" />
                            <span>
                              {timerExpired
                                ? t('Time up – window closed')
                                : t('Start within {time}', {
                                    time: `${Math.floor(timeLeftSeconds / 60)
                                      .toString()
                                      .padStart(2, '0')}:${(timeLeftSeconds % 60)
                                      .toString()
                                      .padStart(2, '0')}`,
                                  })}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-xs text-xs leading-snug">
                          {t('You need to start the AI interview within 10 minutes of opening this page. After that, your screening window may automatically close based on your deadline.')}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>

              {/* Body: guidance row + two-column layout */}
              <div className="p-4 sm:p-6 lg:p-7">
                {/* Guidance row: 2x2 grid similar to assessment page */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-5 mb-4 sm:mb-6">
                  <div className="flex gap-2 sm:gap-3">
                    <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-electric-blue-50 flex items-center justify-center">
                      <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:text-sm">
                        {t('Takes about 2 mins')}
                      </h3>
                      <p className="text-xs text-gray-600 sm:text-sm">
                        {t('Complete the interview in one sitting.')}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 sm:gap-3">
                    <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-electric-blue-50 flex items-center justify-center">
                      <SparklesIcon className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:text-sm">
                        {t('Be natural and confident')}
                      </h3>
                      <p className="text-xs text-gray-600 sm:text-sm">
                        {t('Speak clearly and take your time.')}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 sm:gap-3">
                    <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-electric-blue-50 flex items-center justify-center">
                      <ShieldCheckIcon className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:text-sm">
                        {t('This interview is proctored')}
                      </h3>
                      <p className="text-xs text-gray-600 sm:text-sm">
                        {t("Please don't use external help.")}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 sm:gap-3">
                    <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-electric-blue-50 flex items-center justify-center">
                      <SignalIcon className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:text-sm">
                        {t('Quiet spot & strong Wi‑Fi')}
                      </h3>
                      <p className="text-xs text-gray-600 sm:text-sm">
                        {t('Minimize distractions around you.')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Two-column layout: job details (left) + form (right) */}
                <div className="mt-2 gap-4 sm:gap-6 lg:grid lg:grid-cols-2 items-start">
                  {/* Left card – Job / interview details */}
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 lg:p-8">
                    <h2 className="text-lg font-bold text-gray-900 sm:text-xl">{jobTitle}</h2>
                    <div className="mt-1.5 sm:mt-2 inline-flex rounded-full bg-electric-blue-100 px-2.5 py-0.5 sm:px-3 sm:py-1 text-xs font-medium text-electric-blue-800">
                      {t(getInterviewStepLabel(effectiveInterviewType))}
                    </div>
                    <p className="mt-3 text-xs text-gray-600 sm:mt-4 sm:text-sm">
                      {companyName}
                      {interview.jobPosting?.location && (
                        <span> · {interview.jobPosting.location}</span>
                      )}
                    </p>

                    {hasDetails && (
                      <div className="mt-3 pt-3 border-t border-gray-100 sm:mt-4 sm:pt-4">
                        <button
                          type="button"
                          onClick={() => setDetailsExpanded((prev) => !prev)}
                          className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-electric-blue transition-colors"
                        >
                          <span>{t('View job and interview details')}</span>
                          {detailsExpanded ? (
                            <ChevronUpIcon className="w-4 h-4 flex-shrink-0 ml-2" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4 flex-shrink-0 ml-2" />
                          )}
                        </button>
                        {detailsExpanded && (
                          <div className="mt-3 text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                            {getRoleOverview}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mt-3 flex items-center gap-2 text-xs text-gray-600 sm:mt-4 sm:text-sm">
                      <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-lg bg-electric-blue-100">
                        {interview?.type === 'video' ? (
                          <VideoCameraIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-electric-blue" />
                        ) : (
                          <MicrophoneIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-electric-blue" />
                        )}
                      </div>
                      <span>
                        {t(interview?.type === 'video' ? 'Video interview with AI' : 'Voice interview with AI')}
                      </span>
                    </div>
                  </div>

                  {/* Right card – Basic details form; compact on mobile */}
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 lg:p-8 mt-4 sm:mt-6 lg:mt-0">
                    <h2 className="text-lg font-bold text-gray-900 sm:text-xl">{t('Get ready for your interview!')}</h2>
                    <p className="mt-0.5 sm:mt-1 text-xs text-gray-600 sm:text-sm">
                      {t('Enter your details to continue to the interview.')}
                    </p>

                    <form
                      onSubmit={handleJoin}
                      className="mt-4 space-y-3 sm:mt-6 sm:space-y-4"
                      aria-describedby={errorMessage ? 'interview-join-error' : undefined}
                    >
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('Full name')}<span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                            <UserIcon className="h-5 w-5" />
                          </div>
                          <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={(e) => {
                              if (isNameLocked) return;
                              setName(e.target.value);
                            }}
                            readOnly={isNameLocked}
                            required
                            className={`block w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-electric-blue focus:outline-none focus:ring-1 focus:ring-electric-blue sm:py-2.5 ${
                              isNameLocked ? 'bg-gray-50 cursor-not-allowed' : ''
                            }`}
                            data-testid="candidate-name-input"
                            placeholder={t('Your full name')}
                            title={
                              isNameLocked ? t('Prefilled from the link and cannot be changed.') : undefined
                            }
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('Email')}<span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                            <EnvelopeIcon className="h-5 w-5" />
                          </div>
                          <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => {
                              if (isEmailLocked) return;
                              setEmail(e.target.value);
                            }}
                            readOnly={isEmailLocked}
                            required
                            className={`block w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-electric-blue focus:outline-none focus:ring-1 focus:ring-electric-blue sm:py-2.5 ${
                              isEmailLocked ? 'bg-gray-50 cursor-not-allowed' : ''
                            }`}
                            data-testid="candidate-email-input"
                            placeholder="you@example.com"
                            title={
                              isEmailLocked ? t('Prefilled from the link and cannot be changed.') : undefined
                            }
                          />
                        </div>
                      </div>

                      {errorMessage && (
                        <div
                          id="interview-join-error"
                          className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
                          role="alert"
                        >
                          {t(errorMessage)}
                        </div>
                      )}

                      <button
                        type="submit"
                        data-testid="candidate-authenticate-btn"
                        disabled={authenticating || timerExpired}
                        className="w-full flex items-center justify-center px-6 py-2.5 sm:py-3.5 bg-purple-600 text-white rounded-xl font-medium text-sm sm:text-base hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                      >
                        {timerExpired ? (
                          <>{t("Time's up")}</>
                        ) : authenticating ? (
                          <>
                            <div
                              className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"
                              aria-hidden
                            />
                            {t('Verifying…')}
                          </>
                        ) : (
                          <>
                            {t('Next')}
                            <ArrowRightIcon className="h-4 w-4" aria-hidden />
                          </>
                        )}
                      </button>
                    </form>

                    <p className="mt-3 text-center text-[11px] text-gray-500 sm:mt-4 sm:text-xs">
                      {t('Need help?')}{' '}
                      <a
                        href="mailto:admin@indaratech.net"
                        className="text-electric-blue hover:text-electric-blue-600 hover:underline"
                      >
                        {t('Contact support')}
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated view – same theme and layout as screening steps
  const includeResumeStep = hasResumeScoring && resumeStepResolved;
  const steps = [
    'Basic details',
    ...(includeResumeStep ? ['Resume upload'] : []),
    ...(includeDocumentCollection ? ['Document upload'] : []),
    ...(includeApplicationForm ? ['Application form'] : []),
    ...(hasAssessment ? ['Assessment'] : []),
    'AI interview',
  ];
  const currentStepIndex = steps.findIndex((s) => s === 'AI interview');
  const effectiveInterviewType = interview?.type === 'video' ? 'video' : 'voice';
  const stepMetaMap = getStepMeta(effectiveInterviewType);

  return (
    <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
      <LeaveScreeningModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onStay={() => setShowLeaveModal(false)}
        onLeave={() => { setShowLeaveModal(false); navigate(-1); }}
        title={t('Leave this page?')}
        message={t('Refreshing or closing this page may auto-submit your assessment and affect your progress. Please stay on this page until you complete this step or your time runs out.')}
      />
      <div className="mx-auto max-w-6xl">
        {compactHeader}

        {steps.length > 0 && (
          <section className="mt-6 sm:mt-8">
            <p className="text-center text-[11px] sm:text-sm font-semibold uppercase tracking-[0.22em] text-gray-500 mb-5 sm:mb-7">
              {t('Your screening journey')}
            </p>
            <div className="flex items-start justify-center gap-0">
              {steps.map((label, index) => {
                const meta =
                  stepMetaMap[label] || {
                    icon: CheckIcon,
                    desc: label === 'AI interview' ? getInterviewStepSecondary(interview?.advancedType) : (STEP_SECONDARY[label] || ''),
                    gradient: 'from-gray-400 to-gray-500',
                  };
                const StepIcon = meta.icon;
                const isActive = index === currentStepIndex;
                const isCompleted = index < currentStepIndex;
                return (
                  <React.Fragment key={label}>
                    <div className="flex flex-col items-center text-center min-w-0 flex-1 sm:flex-none sm:w-32">
                      <span
                        className={`text-[9px] sm:text-xs font-semibold uppercase tracking-[0.06em] sm:tracking-[0.16em] whitespace-nowrap mb-1.5 ${
                          isActive ? 'text-electric-blue' : isCompleted ? 'text-emerald-400' : 'text-gray-300'
                        }`}
                      >
                        {t('Step {number}', { number: index + 1 })}
                      </span>
                      <div
                        className={`relative flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center rounded-2xl transition-all duration-500 ${
                          isActive
                            ? `bg-gradient-to-br ${meta.gradient} text-white shadow-lg`
                            : isCompleted
                            ? 'bg-emerald-50 text-emerald-500 border-2 border-emerald-200'
                            : 'bg-white text-gray-300 border-2 border-gray-200 border-dashed'
                        }`}
                        style={
                          isActive
                            ? { boxShadow: '0 8px 25px rgba(0, 123, 255, 0.2)' }
                            : isCompleted
                            ? { boxShadow: '0 6px 18px rgba(16, 185, 129, 0.18)' }
                            : {}
                        }
                      >
                        {isCompleted ? (
                          <CheckIcon className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.5} />
                        ) : (
                          <StepIcon className="h-5 w-5 sm:h-7 sm:w-7" strokeWidth={isActive ? 2 : 1.5} />
                        )}
                        {isActive && (
                          <span
                            className="absolute inset-0 rounded-2xl border-2 border-electric-blue/30 animate-ping"
                            style={{ animationDuration: '2s' }}
                          />
                        )}
                      </div>
                      <p
                        className={`mt-2 text-[10px] sm:text-sm font-semibold leading-tight hyphens-auto [overflow-wrap:anywhere] ${
                          isActive ? 'text-gray-900' : isCompleted ? 'text-emerald-600' : 'text-gray-400'
                        }`}
                      >
                        {t(label === 'AI interview' ? getInterviewStepLabel(effectiveInterviewType) : label)}
                      </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="flex-1 flex items-center pt-[52px] sm:pt-[52px] min-w-[6px] max-w-[12px] sm:max-w-[72px]">
                        <div className="w-full flex items-center gap-0.5">
                          {[...Array(4)].map((_, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <div
                              key={i}
                              className={`flex-1 h-0.5 rounded-full ${
                                index < currentStepIndex ? 'bg-emerald-400/60' : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </section>
        )}

        {/* Main card – aligned with assessment theme */}
        <div className="mt-4 sm:mt-6 max-w-5xl mx-auto">
          <div className="bg-white rounded-3xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
            {/* Header strip */}
            <div className="bg-electric-blue px-5 py-4 sm:px-6 sm:py-5">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                    {t('Start your interview')}
                  </h1>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 sm:px-3.5 sm:py-1.5 text-[10px] sm:text-sm font-semibold text-electric-blue-700 shadow-sm whitespace-nowrap">
                    <ClockIcon className="h-4 w-4 text-electric-blue-500" />
                    <span>
                      {timerExpired
                        ? t('Time up – window closed')
                        : t('Start within {time}', {
                            time: `${Math.floor(timeLeftSeconds / 60)
                              .toString()
                              .padStart(2, '0')}:${(timeLeftSeconds % 60)
                              .toString()
                              .padStart(2, '0')}`,
                          })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-4 sm:p-6 lg:p-7">
              {/* Duration + key info row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-5 mb-4 sm:mb-6 lg:mb-8">
                <div className="flex gap-2 sm:gap-3">
                  <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-electric-blue-50 flex items-center justify-center">
                    <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:text-sm">
                      {t('Takes about 2 mins')}
                    </h3>
                    <p className="text-xs text-gray-600 sm:text-sm">
                      {t('Complete the interview in one sitting.')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 sm:gap-3">
                  <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-electric-blue-50 flex items-center justify-center">
                    <ShieldCheckIcon className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:text-sm">
                      {t('This interview is proctored')}
                    </h3>
                    <p className="text-xs text-gray-600 sm:text-sm">
                      {t("Don't use external help or leave the session.")}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 sm:gap-3">
                  <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-electric-blue-50 flex items-center justify-center">
                    <SpeakerWaveIcon className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:text-sm">
                      {t('Be natural and confident')}
                    </h3>
                    <p className="text-xs text-gray-600 sm:text-sm">
                      {t('Speak clearly and take your time.')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 sm:gap-3">
                  <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-electric-blue-50 flex items-center justify-center">
                    <SignalIcon className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:text-sm">
                      {t('Quiet spot & strong Wi‑Fi')}
                    </h3>
                    <p className="text-xs text-gray-600 sm:text-sm">
                      {t('Minimize distractions around you.')}
                    </p>
                  </div>
                </div>
                {interview?.type === 'video' && (
                  <div className="flex gap-2 sm:gap-3">
                    <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-electric-blue-50 flex items-center justify-center">
                      <VideoCameraIcon className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:text-sm">{t('Camera on throughout')}</h3>
                      <p className="text-xs text-gray-600 sm:text-sm">
                        {t('Position yourself clearly in the frame with good lighting.')}
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex gap-2 sm:gap-3">
                  <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-electric-blue-50 flex items-center justify-center">
                    <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-gray-900 mb-0.5 sm:text-sm">{t('Reviewed by a human')}</h3>
                    <p className="text-xs text-gray-600 sm:text-sm">
                      {t('A recruiter will review your interview.')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Before you begin – amber warning */}
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-4 sm:p-5 sm:mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <ExclamationCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <h3 className="text-sm font-bold text-amber-900">{t('Before you begin')}</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-1">
                  <div className="flex gap-2.5 items-start">
                    <VideoCameraIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm text-amber-800 leading-snug">
                      <strong>{t('Camera & Audio:')}</strong> {t('You will be recorded via webcam and microphone')}
                    </p>
                  </div>
                  <div className="flex gap-2.5 items-start">
                    <UserIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm text-amber-800 leading-snug">
                      <strong>{t('Attire:')}</strong> {t('Please dress appropriately (formal or professional attire)')}
                    </p>
                  </div>
                  <div className="flex gap-2.5 items-start">
                    <SparklesIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm text-amber-800 leading-snug">
                      <strong>{t('Environment:')}</strong> {t('Ensure a quiet, well-lit space with stable internet')}
                    </p>
                  </div>
                  <div className="flex gap-2.5 items-start">
                    <ClockIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm text-amber-800 leading-snug">
                      <strong>{t('Important:')}</strong> {t('Start within 2 mins. Cannot be restarted once begun')}
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA – full-width brand button; compact on mobile */}
              <button
                onClick={startInterview}
                data-testid="join-interview-btn"
                disabled={timerExpired}
                className="w-full flex items-center justify-center px-6 py-2.5 sm:py-3.5 bg-gradient-to-r from-electric-blue to-indigo-600 hover:from-electric-blue/90 hover:to-indigo-700 text-white text-sm sm:text-base font-bold rounded-lg shadow-lg shadow-electric-blue/20 hover:shadow-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {timerExpired ? t("Time's up") : t('Start Interview')}
              </button>
            </div>

            {/* Device Check Modal */}
            <DeviceCheckModal
              isOpen={showDeviceCheck}
              onClose={() => setShowDeviceCheck(false)}
              onProceed={proceedToInterview}
              interviewType={interview?.type === 'video' ? 'video' : 'voice'}
            />

            {/* Voice + camera readiness check (org feature ai_interview_readiness_check) */}
            <InterviewReadinessModal
              isOpen={showReadinessCheck}
              onClose={() => setShowReadinessCheck(false)}
              onProceed={proceedToInterview}
              interviewType={interview?.type === 'video' ? 'video' : 'voice'}
              sessionId={sessionData?.sessionId}
              sessionToken={sessionData?.sessionToken}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIInterviewJoin;
